import * as THREE from 'three';
import type { PlayerPublic, PlayerSnapshot, Weather, World } from '../../shared/types.js';
import {
  carWorldFor,
  createCarState,
  carPose,
  FixedClock,
  forwardSpeed,
  lerpPose,
  stepCarFixed,
  surfaceHeight,
  type CarPose,
  type CarState,
  type CarWorld,
  type OtherCar,
  DEFAULT_TUNING,
} from '../../shared/car.js';
import { autopilotInput } from '../../shared/autopilot.js';
import { headingFromDirection, projectOntoPolyline } from '../../shared/geom.js';
import { crossedFinish, passedThrough } from '../../shared/gates.js';
import { ROAD_SPECS } from '../../shared/osm/classify.js';
import { graphicsPrefs, VIEW_DISTANCE_METRES } from '../graphicsPrefs.js';
import { createSceneKit, type SceneKit } from '../render/scene.js';
import { buildWorldMeshes, TRIANGLE_BUDGET, type WorldMeshes } from '../render/worldMesh.js';
import { buildBeacon, buildRouteMeshes, type RouteMeshes } from '../render/routeMesh.js';
import { buildRailTraffic, type RailTrafficMeshes } from '../render/trains.js';
import { createCarMesh, type CarMesh } from '../render/carMesh.js';
import { carAudio } from '../audio.js';
import { ambience } from '../ambience.js';
import { ChaseCamera } from './chaseCamera.js';
import { OutroCamera, Skyline, type OutroSubject } from './outroCamera.js';
import { FlyoverCamera } from './flyoverCamera.js';
import { InputController } from './input.js';
import { Minimap, type MinimapMode, type MinimapPlayer } from './minimap.js';
import { shouldShowTouchControls, TouchControls } from '../ui/touchControls.js';
import { num, t } from '../i18n/i18n.js';
import { fpsOf, SampleRing } from '../sampleRing.js';
import { StreetIndex, StreetTracker } from './streets.js';
import { signStyleAt, type SignStyle } from '../ui/streetSign.js';
import type { MapSnapshot } from '../ui/bigMap.js';

/** How far behind live we render other players, in ms. Two snapshots at 20 Hz. */
const INTERP_DELAY_MS = 110;
/**
 * How long a remote car may go without a position update before it stops being solid.
 *
 * Comfortably more than the gap between two snapshots at `SNAPSHOT_HZ` (50 ms) plus the
 * interpolation delay, so ordinary jitter or a dropped packet never makes a live opponent
 * flicker out of the collision set — and far less than the minute the server holds a
 * disconnected player's slot for. See `remoteCars`.
 */
const STALE_CAR_MS = 700;
/** Rate at which we tell the server where we are. */
const STATE_HZ = 20;
/** A free-drive beacon is gone within this many metres of the camera, and full past this much more. */
const BEACON_NEAR = 18;
const BEACON_FADE = 40;

/**
 * Impact speeds, m/s, that separate not-worth-a-sound from a graze from a real hit.
 *
 * `CarState.impact` is the speed the car went *into* something at, and it survives one
 * physics step. The floor is above the tiny impacts a car resting against a wall generates
 * every step; the ceiling is the same order as the skid's own threshold in `audio.ts`, so a
 * heavy crash is a hard clip and a skid rather than a hard clip alone.
 */
const CRASH_SOFT_SPEED = 3.5;
const CRASH_HARD_SPEED = 9;
/**
 * Speed across a kerb, m/s, below which going over it is not worth a sound — running along
 * the kerb line puts a car on and off it at a few centimetres a second. Above it, the graze
 * cue, quieter than a wall at the same speed: a kerb is a thump, not a crash (§93).
 */
const KERB_THUMP_SPEED = 1.2;

/** Frames the low-frame-rate warning is judged over. */
const PERF_WINDOW = 240;
/**
 * Below this, two windows running, a machine on the guessed `high` setting is put on `low`
 * (DECISIONS.md §86). Under 60 rather than at it, because a display that refreshes at 60
 * reads as 59 and change on a good day.
 */
const AUTO_LOW_FPS = 48;
/** Frames kept for the perf graph: four seconds at 120 Hz, eight at 60. */
const FRAME_HISTORY = 480;

export interface HudData {
  speedKmh: number;
  onRoad: boolean;
  checkpointsPassed: number;
  checkpointsTotal: number;
  raceTimeMs: number;
  position: number;
  playerCount: number;
  countdown: number | null;
  finished: boolean;
  distanceToNext: number;
  /** True once this player has crossed the line and the flyover has taken over. */
  spectating: boolean;
  /** Whose car the flyover is following; null when it is circling the finish line. */
  spectatingName: string | null;
  /** On the grid, watching the route from the air. Every in-race readout is meaningless here. */
  staging: boolean;
  /** Driving round with no gates and no clock, so the race readouts have nothing to say. */
  freeDrive: boolean;
  /** The street the car last settled on, by name; null before the first. See `StreetTracker`. */
  street: string | null;
}

export interface GameCallbacks {
  onState(x: number, y: number, z: number, heading: number, speed: number): void;
  onHud(data: HudData): void;
  /** Fired once when the local car crosses the finish line, for immediate UI feedback. */
  onLocalFinish(): void;
  onPerfWarning(message: string): void;
  /** The horn, from the touch layer. Needs the socket, which lives in `main.ts`. */
  onHonk(): void;
  /** The Escape menu, from the touch layer's button. The menu lives in `main.ts`. */
  onMenu(): void;
  /** The car has settled onto a new street; the plate is in the region's country's style. */
  onStreet(name: string, style: SignStyle): void;
  /**
   * The server's clock in ms, through `ClockSync`. The trains run on it, so everyone sees the
   * same one; without it they run on this machine's clock, which is close but not shared.
   */
  serverNow?(): number;
}

interface RemoteSample {
  t: number;
  x: number;
  y: number;
  z: number;
  h: number;
}

interface RemoteCar {
  mesh: CarMesh;
  samples: RemoteSample[];
  color: number;
  speed: number;
  lastSteer: number;
  /** A column of light over the car in a free drive — see `updateBeacon`. Made when first needed. */
  beacon: ReturnType<typeof buildBeacon> | null;
}

/**
 * The driving side of the client: owns the scene, runs the car, interpolates everyone
 * else and reports position upstream.
 *
 * It knows nothing about lobbies or WebSockets — the app feeds it snapshots and it hands
 * back HUD data — which is what lets the whole thing be driven from a test without a
 * server.
 */
export class Game {
  private readonly kit: SceneKit;
  private readonly touchControls: TouchControls | null;
  private readonly input: InputController;
  private readonly chase = new ChaseCamera();
  private readonly outro = new OutroCamera();
  /** The establishing shot on the grid: a drone flying the route. */
  private readonly showcase = new FlyoverCamera();
  /** Last gate count handed to the route while the showcase drives it, so it is set once per gate. */
  private showcaseGate = -1;
  private readonly minimap: Minimap;
  private streets: StreetIndex | null = null;
  private readonly streetTracker = new StreetTracker();
  private signStyle: SignStyle = 'plain';
  private readonly clock = new THREE.Clock();

  private world: World | null = null;
  private worldMeshes: WorldMeshes | null = null;
  private routeMeshes: RouteMeshes | null = null;
  private railMeshes: RailTrafficMeshes | null = null;
  private carWorld: CarWorld | null = null;

  private car: CarState = createCarState(0, 0, 0);
  /**
   * The physics runs at a fixed 120 Hz whatever the display does, so a frame lands
   * somewhere between two steps — on a 144 Hz monitor, often with no step at all. The car
   * is drawn `clock.alpha` of the way from `prevPose` to `car`, which is up to one step
   * (8 ms) behind the simulation and never jumps. Without it, a 144 Hz frame that ran no
   * physics would draw the car exactly where the last one did.
   */
  private readonly physicsClock = new FixedClock();
  private prevPose: CarPose = carPose(this.car);
  /** `car` at the interpolated pose: what the mesh and the cameras look at. */
  private readonly viewCar: CarState = createCarState(0, 0, 0);
  private carMesh: CarMesh | null = null;
  private localColor = 0xe8503a;
  private localId = '';

  private readonly remotes = new Map<string, RemoteCar>();
  /** The cars as the minimap last drew them, for the street map. */
  private mapPlayers: MinimapPlayer[] = [];
  private roster: PlayerPublic[] = [];

  private running = false;
  private frameHandle = 0;
  private lastStateSent = 0;
  private raceStartAt: number | null = null;
  private countdownUntil: number | null = null;
  /** Index of the gate this client is heading for. Public so the e2e suite can read it. */
  nextCheckpoint = 0;
  private finished = false;
  /** In a free drive: no gates, no finish, no route on screen. See `beginFreeDrive`. */
  private freeDrive = false;
  /** True from the moment this player crosses the line until the next race is set up. */
  private spectating = false;
  /** Whose car the flyover is following. Held until they finish, so the shot is stable. */
  private subjectId: string | null = null;
  private resizeObserver: ResizeObserver | null = null;

  /** Last live placing handed to the HUD, so a change in it can be heard. 0 until the
   *  first frame of a race, which is what stops the opening placing being an overtake. */
  private lastPosition = 0;
  /**
   * Frame times in seconds, for the perf warning, the e2e probe and the perf graph. A ring,
   * so there is never a moment with nothing in it — see `SampleRing`.
   */
  readonly frameTimes = new SampleRing(FRAME_HISTORY);
  private framesSinceCheck = 0;
  private perfWarned = false;
  /** Consecutive perf windows under `AUTO_LOW_FPS`. */
  private slowWindows = 0;
  /** Seconds the autopilot has spent going nowhere; see the respawn safety net. */
  private stalledFor = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    minimapCanvas: HTMLCanvasElement,
    private readonly callbacks: GameCallbacks,
  ) {
    // The strike is the renderer's — it is a light — and the clap is the environment's,
    // which is why the two are joined here rather than either one owning both.
    this.kit = createSceneKit(canvas, { onLightning: () => ambience.thunder() });
    this.applyGraphics();
    this.unsubscribeGraphics = graphicsPrefs.onChange(this.applyGraphics);
    // Built before the InputController so it can be handed straight in — see TouchInput
    // in input.ts. Respawn and the camera toggle are wired directly to this instance rather
    // than through GameCallbacks, since both are already plain methods here; only the HUD
    // banner text keyboard R/F also trigger is left to main.ts's onGlobalKey.
    this.touchControls = shouldShowTouchControls()
      ? new TouchControls({
          onRespawn: () => this.respawn(),
          onToggleCamera: () => this.setCameraMode(this.cameraMode === 'chase' ? 'aerial' : 'chase'),
          onHonk: () => this.callbacks.onHonk(),
          onMenu: () => this.callbacks.onMenu(),
        })
      : null;
    if (this.touchControls) (canvas.parentElement ?? document.body).append(this.touchControls.root);
    this.input = new InputController(window, this.touchControls);
    this.minimap = new Minimap(minimapCanvas);
    this.handleResize();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(canvas);
    }
    window.addEventListener('resize', this.handleResize);
  }

  private readonly unsubscribeGraphics: () => void;

  /** The Escape menu's two graphics choices, applied whenever either changes. */
  private applyGraphics = (): void => {
    this.kit.setQuality(graphicsPrefs.quality);
    this.worldMeshes?.setQuality(graphicsPrefs.quality);
    this.chase.setView(graphicsPrefs.chaseView);
  };

  private handleResize = (): void => {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    this.kit.resize(width, height);
  };

  // -------------------------------------------------------------------------
  // world
  // -------------------------------------------------------------------------

  setWorld(world: World): void {
    this.clearWorld();
    this.world = world;

    this.worldMeshes = buildWorldMeshes(world);
    this.kit.scene.add(this.worldMeshes.group);

    this.routeMeshes = buildRouteMeshes(world);
    this.routeMeshes.group.visible = !this.freeDrive;
    this.kit.scene.add(this.routeMeshes.group);

    this.carWorld = carWorldFor(world);
    this.chase.setGround(this.carWorld.ground ?? null);
    this.chase.setBuildings(this.carWorld.buildings);
    this.railMeshes = this.carWorld.rail ? buildRailTraffic(world, this.carWorld.rail, this.carWorld.ground) : null;
    for (const object of this.railMeshes?.objects ?? []) this.kit.scene.add(object);
    this.minimap.setWorld(world);
    // One skyline, two cameras. It is a full pass over every building in the region, and
    // both the outro and the grid's showcase want exactly the same answer from it.
    const skyline = new Skyline(world);
    this.outro.setSkyline(skyline);
    this.showcase.setWorld(world, skyline);
    this.streets = new StreetIndex(world);
    this.streetTracker.reset();
    this.signStyle = signStyleAt(world.origin.lat, world.origin.lon);

    if (this.worldMeshes.triangles > TRIANGLE_BUDGET) {
      this.callbacks.onPerfWarning(
        t('This region is {triangles} triangles, over the {budget} budget. Expect frame drops on a laptop.', {
          triangles: num(this.worldMeshes.triangles),
          budget: num(TRIANGLE_BUDGET),
        }),
      );
    }

    if (!this.carMesh) {
      this.carMesh = createCarMesh(this.localColor);
      this.kit.scene.add(this.carMesh.group);
    }
    // The scene kit outlives the world, so a region loaded after the weather arrived — every
    // race after the first one in a tour — would otherwise come up in broad daylight inside
    // a night sky.
    this.worldMeshes.setNight(this.kit.look.night);
    this.worldMeshes.setWet(this.kit.look.wet);
    this.worldMeshes.setQuality(graphicsPrefs.quality);
    this.railMeshes?.setNight(this.kit.look.night);
    this.applyCarLights();
    this.resetToStart();
  }

  private clearWorld(): void {
    if (this.worldMeshes) {
      this.kit.scene.remove(this.worldMeshes.group);
      this.worldMeshes.dispose();
      this.worldMeshes = null;
    }
    if (this.routeMeshes) {
      this.kit.scene.remove(this.routeMeshes.group);
      this.routeMeshes.dispose();
      this.routeMeshes = null;
    }
    if (this.railMeshes) {
      for (const object of this.railMeshes.objects) this.kit.scene.remove(object);
      this.railMeshes.dispose();
      this.railMeshes = null;
    }
    for (const [id, remote] of this.remotes) {
      this.kit.scene.remove(remote.mesh.group);
      remote.mesh.dispose();
      this.disposeBeacon(remote);
      carAudio.removeRemote(id);
      this.remotes.delete(id);
    }
    this.carWorld = null;
  }

  get hasWorld(): boolean {
    return this.world !== null;
  }

  get triangleCount(): number {
    return this.worldMeshes?.triangles ?? 0;
  }

  /** How many other players' cars are currently in the scene. */
  get remoteCount(): number {
    return this.remotes.size;
  }

  // -------------------------------------------------------------------------
  // players
  // -------------------------------------------------------------------------

  setLocalPlayer(id: string, color: number): void {
    this.localId = id;
    this.localColor = color;
    this.carMesh?.setColor(color);
  }

  setRoster(players: PlayerPublic[]): void {
    this.roster = players;
    const ids = new Set(players.map((p) => p.id));
    for (const [id, remote] of this.remotes) {
      if (ids.has(id)) continue;
      this.kit.scene.remove(remote.mesh.group);
      remote.mesh.dispose();
      this.disposeBeacon(remote);
      carAudio.removeRemote(id);
      this.remotes.delete(id);
    }
    for (const player of players) {
      if (player.id === this.localId) {
        if (player.color !== this.localColor) {
          this.localColor = player.color;
          this.carMesh?.setColor(player.color);
        }
        continue;
      }
      const existing = this.remotes.get(player.id);
      if (existing) {
        if (existing.color !== player.color) {
          existing.color = player.color;
          existing.mesh.setColor(player.color);
        }
      }
    }
  }

  /** Places the local car on the grid. Called on race start and on respawn. */
  setSpawn(x: number, z: number, heading: number): void {
    this.car = createCarState(x, z, heading);
    // Otherwise the car is placed at Y = 0 and spends the first frame of the countdown
    // buried in, or hovering over, whatever the grid happens to sit on.
    this.car.y = this.carWorld ? surfaceHeight(x, z, this.carWorld) : 0;
    // A respawn is a teleport, and interpolating across one draws the car sliding sideways
    // through the scenery for a frame.
    this.prevPose = carPose(this.car);
    this.physicsClock.reset();
    Object.assign(this.viewCar, this.car);
    this.chase.reset();
    this.syncCarMesh();
  }

  resetToStart(): void {
    const route = this.world?.route;
    if (!route) return;
    const heading = headingFromDirection(route.start.dx, route.start.dz);
    this.setSpawn(route.start.x - route.start.dx * 6, route.start.z - route.start.dz * 6, heading);
    this.resetProgress();
  }

  get route(): World['route'] | null {
    return this.world?.route ?? null;
  }

  /** Clears checkpoint progress without moving the car. Used when a new race starts. */
  resetProgress(): void {
    this.nextCheckpoint = 0;
    this.finished = false;
    this.lastPosition = 0;
    this.routeMeshes?.setNextCheckpoint(0);
    this.stopSpectating();
  }

  applySnapshot(snapshot: { now: number; players: PlayerSnapshot[] }): void {
    const now = performance.now();
    for (const p of snapshot.players) {
      // Fold the live figures back into the roster. `lobby` messages only arrive on state
      // changes, so without this the roster's progress stays at whatever it was when the
      // race started — and the live position readout would never move off 1st.
      const rosterEntry = this.roster.find((r) => r.id === p.id);
      if (rosterEntry) {
        rosterEntry.progress = p.p;
        rosterEntry.nextCheckpoint = p.c;
      }
      if (p.id === this.localId) continue;
      let remote = this.remotes.get(p.id);
      if (!remote) {
        const color = this.roster.find((r) => r.id === p.id)?.color ?? 0x8899aa;
        const mesh = createCarMesh(color);
        // A car that joins the snapshot stream after dark has to arrive with its lights
        // already on — the weather is applied once, long before this one existed.
        mesh.setLights(this.kit.look.night);
        this.kit.scene.add(mesh.group);
        remote = { mesh, samples: [], color, speed: 0, lastSteer: 0, beacon: null };
        this.remotes.set(p.id, remote);
      }
      remote.samples.push({ t: now, x: p.x, y: p.y, z: p.z, h: p.h });
      remote.speed = p.v;
      // Two samples of history is all the interpolator ever looks at.
      while (remote.samples.length > 6) remote.samples.shift();
    }
  }

  // -------------------------------------------------------------------------
  // race state
  // -------------------------------------------------------------------------

  /**
   * On the grid: the camera takes off behind the start line and flies the route while
   * everyone reads the map and says they are ready. Nothing is driveable and nothing is timed.
   */
  beginStaging(): void {
    this.input.enabled = false;
    this.input.clear();
    this.setFreeDrive(false);
    this.finished = false;
    this.raceStartAt = null;
    this.countdownUntil = null;
    this.stopSpectating();
    this.showcase.reset();
    this.showcaseGate = -1;
    // Every race opens on a sign for the street the grid is on, once the car is moving.
    this.streetTracker.reset();
    carAudio.silence();
  }

  private endStaging(): void {
    if (!this.showcase.running) return;
    this.showcase.stop();
    this.chase.reset();
    // The showcase lit the gates as it flew over them; the race starts with none passed.
    this.routeMeshes?.setNextCheckpoint(this.nextCheckpoint);
    this.canvas.style.filter = '';
    this.kit.setFogRange(null);
    // The showcase changes the lens the way the outro does, and the chase camera assumes it
    // gets it back at 55.
    this.kit.camera.fov = 55;
    this.kit.camera.updateProjectionMatrix();
  }

  get isStaging(): boolean {
    return this.showcase.running;
  }

  /**
   * The conditions at the region, straight through to the renderer — and, separately, to
   * the city itself, which has to turn its own lights on.
   */
  applyWeather(weather: Weather | null): void {
    this.kit.applyWeather(weather);
    this.worldMeshes?.setNight(this.kit.look.night);
    this.worldMeshes?.setWet(this.kit.look.wet);
    this.railMeshes?.setNight(this.kit.look.night);
    this.applyCarLights();
  }

  /** Every car on the grid runs the same dusk ramp as the city around it. */
  private applyCarLights(): void {
    const night = this.kit.look.night;
    this.carMesh?.setLights(night);
    for (const remote of this.remotes.values()) remote.mesh.setLights(night);
  }

  /**
   * `localStartAt` is the start converted through the synced clock (`ClockSync`), when one
   * exists. Without it, the message's own `now` converts it — which is exactly one network
   * trip late, since `now` was true when the message left. The synced answer is only
   * believed within a second of that, earlier and never later: a sync that disagrees by
   * more is a broken sample, and the old answer is late but never wrong by more than ping.
   */
  beginCountdown(startsAtServerMs: number, serverNowMs: number, localStartAt?: number | null): void {
    this.endStaging();
    const received = performance.now() + (startsAtServerMs - serverNowMs);
    this.countdownUntil =
      localStartAt != null && Number.isFinite(localStartAt)
        ? Math.min(received, Math.max(received - 1000, localStartAt))
        : received;
    this.raceStartAt = this.countdownUntil;
    this.finished = false;
    this.input.enabled = false;
    this.input.clear();
    this.stopSpectating();
    carAudio.playCountdown(this.countdownUntil - performance.now());
  }

  beginRacing(): void {
    if (this.raceStartAt === null) this.raceStartAt = performance.now();
    this.countdownUntil = null;
    this.input.enabled = true;
    carAudio.playStart();
  }

  /**
   * Straight into the car, with no fly-over and no lights — the server has already started
   * the clock it does not keep. The route is still in the world (every world is built round
   * one) but nobody is following it, so it comes off the road and off the map.
   */
  beginFreeDrive(): void {
    this.endStaging();
    this.stopSpectating();
    this.setFreeDrive(true);
    this.finished = false;
    this.countdownUntil = null;
    this.raceStartAt = performance.now();
    this.input.enabled = true;
  }

  /** Back to the lobby from a free drive: the car stops being drivable and the route returns. */
  endFreeDrive(): void {
    if (!this.freeDrive) return;
    this.setFreeDrive(false);
    this.input.enabled = false;
    this.input.clear();
    carAudio.silence();
  }

  get isFreeDrive(): boolean {
    return this.freeDrive;
  }

  private setFreeDrive(on: boolean): void {
    this.freeDrive = on;
    if (this.routeMeshes) this.routeMeshes.group.visible = !on;
    this.minimap.setRouteVisible(!on);
  }

  endRace(): void {
    this.input.enabled = false;
    this.input.clear();
    carAudio.silence();
    this.beginSpectating();
  }

  // -------------------------------------------------------------------------
  // the outro
  // -------------------------------------------------------------------------

  /**
   * Lifts the camera off the car and starts the flyover. Called the moment this player
   * crosses the line — not when the race ends — so the wait for everyone else is
   * something to watch rather than a frozen windscreen.
   */
  beginSpectating(): void {
    if (this.spectating) return;
    this.spectating = true;
    this.outro.reset(this.kit.camera, this.pickOutroSubject());
  }

  private stopSpectating(): void {
    this.spectating = false;
    this.outro.stop();
    this.subjectId = null;
    this.chase.reset();
    // The outro widens the lens; the chase camera assumes it got it back.
    this.kit.camera.fov = 55;
    this.kit.camera.updateProjectionMatrix();
  }

  get isSpectating(): boolean {
    return this.spectating;
  }

  /** Who the flyover is watching, for the HUD caption. Null means the finish line. */
  get spectatingName(): string | null {
    if (!this.subjectId) return null;
    return this.roster.find((p) => p.id === this.subjectId)?.name ?? null;
  }

  /**
   * The most interesting thing on the map: whoever is still racing and furthest round.
   * Failing that — everyone home, or racing alone — the finish line, which is where the
   * cars are anyway.
   *
   * The current subject is held until they finish or disappear. Re-picking every frame
   * would swap the shot each time two cars traded places, which is nauseating.
   */
  private pickOutroSubject(): OutroSubject {
    const stillRacing = (p: PlayerPublic) => p.connected && p.finishedAt === null;

    if (this.subjectId) {
      const held = this.roster.find((p) => p.id === this.subjectId);
      if (held && stillRacing(held)) {
        const at = this.subjectPosition(held.id);
        if (at) return { ...at, label: held.name };
      }
      this.subjectId = null;
    }

    let leader: PlayerPublic | null = null;
    for (const player of this.roster) {
      if (player.id === this.localId || !stillRacing(player)) continue;
      if (!this.subjectPosition(player.id)) continue;
      if (!leader || player.progress > leader.progress) leader = player;
    }
    if (leader) {
      const at = this.subjectPosition(leader.id);
      if (at) {
        this.subjectId = leader.id;
        return { ...at, label: leader.name };
      }
    }

    const finish = this.world?.route.finish;
    if (finish) return { x: finish.x, z: finish.z, label: null };
    return { x: this.car.x, z: this.car.z, label: null };
  }

  private subjectPosition(id: string): { x: number; z: number } | null {
    if (id === this.localId) return { x: this.car.x, z: this.car.z };
    const remote = this.remotes.get(id);
    if (!remote) return null;
    const at = remote.mesh.group.position;
    return { x: at.x, z: at.z };
  }

  get isFinished(): boolean {
    return this.finished;
  }

  // -------------------------------------------------------------------------
  // loop
  // -------------------------------------------------------------------------

  start(): void {
    if (this.running) return;
    this.running = true;
    this.clock.start();
    const loop = () => {
      if (!this.running) return;
      this.frameHandle = requestAnimationFrame(loop);
      this.frame();
    };
    this.frameHandle = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    if (this.frameHandle) cancelAnimationFrame(this.frameHandle);
    this.frameHandle = 0;
    // Nothing is calling `update` any more, so the engine gain would otherwise hold
    // wherever it happened to be and hum away over the lobby screen. The weather is in
    // exactly the same position, and for exactly the same reason — but only here, not in
    // `beginStaging`: the city carries on while the camera flies the route.
    carAudio.silence();
    ambience.silence();
  }

  private frame(): void {
    const dt = Math.min(0.1, this.clock.getDelta());
    const now = performance.now();

    this.trackPerf(dt);

    const lightsThisFrame = this.countdownUntil !== null && now >= this.countdownUntil;
    if (lightsThisFrame) this.beginRacing();

    if (this.world && this.carWorld) {
      // Server time, in seconds: what the timetable is a function of.
      const railNow = (this.callbacks.serverNow?.() ?? Date.now()) / 1000;
      this.railMeshes?.update(railNow);
      const input =
        this.autopilot && this.world
          ? autopilotInput(this.car, this.world.route.path, { pace: this.autopilotPace, dt })
          : this.input.sample();
      // Last resort for the autopilot only. Its own reverse-and-turn recovery handles
      // almost everything, but a car wedged in a corner it can neither reverse nor drive
      // out of would sit there forever — and a human would simply press R. Doing it for
      // the autopilot is what stops one bad spawn from hanging a whole race.
      if (this.autopilot && this.input.enabled && !this.finished) {
        this.stalledFor = this.car.speed < 0.4 ? this.stalledFor + dt : 0;
        if (this.stalledFor > 6) {
          this.stalledFor = 0;
          this.respawn();
        }
      }
      if (!this.finished && this.input.enabled) {
        const prevX = this.car.x;
        const prevZ = this.car.z;
        // Whole steps only, with the remainder carried — never `stepCar(dt)`, which rounds
        // the frame to a whole number of steps and once made a 144 Hz player 20 % faster
        // than a 60 Hz one. See DECISIONS.md §59. On the frame the lights go out, only the
        // part of it after them, or a 60 fps player gets up to 16 ms of driving under a red
        // light and a 144 fps one up to 7.
        const drive =
          lightsThisFrame && this.raceStartAt !== null
            ? Math.min(dt, Math.max(0, (now - this.raceStartAt) / 1000))
            : dt;
        const steps = this.physicsClock.advance(drive);
        // Only ever moves the local car — see the contact section of `substep` for why
        // that is safe even though `cars` is up to 110 ms stale (the same interpolated
        // pose `updateRemotes` renders this frame, computed early because that call hasn't
        // run yet this tick) and belongs to players this client does not own.
        const cars = steps > 0 ? this.remoteCars(now) : [];
        const trains = steps > 0 ? this.carWorld.rail?.bodiesAt(railNow) : undefined;
        stepCarFixed(
          this.car,
          input,
          { ...this.carWorld, cars, trains },
          steps,
          DEFAULT_TUNING,
          this.prevPose,
        );
        this.checkProgress(prevX, prevZ);
        this.updateStreet(prevX, prevZ);
      } else {
        // Countdown and post-finish: hold the car still but keep the scene alive.
        this.car.speed = 0;
        this.car.vx = 0;
        this.car.vz = 0;
        this.prevPose = carPose(this.car);
        // Owed time from the countdown would be spent the instant the lights go out.
        this.physicsClock.reset();
      }
      Object.assign(this.viewCar, this.car);
      lerpPose(this.prevPose, this.car, this.physicsClock.alpha, this.viewCar);
      this.syncCarMesh();
      this.carMesh?.animate(forwardSpeed(this.car), this.car.steer, this.car.slip, dt);
      this.carMesh?.setBraking(this.input.braking && forwardSpeed(this.car) > 1);
      carAudio.update(
        {
          speed: forwardSpeed(this.car),
          throttle: input.throttle,
          handbrake: input.handbrake,
          impact: this.car.impact,
          racing: this.input.enabled && !this.finished,
        },
        dt,
      );
      this.updateAmbience(dt);
      this.routeMeshes?.update(dt);
      if (this.showcase.running) this.updateShowcase(dt);
      else if (this.spectating) this.outro.update(this.kit.camera, this.pickOutroSubject(), dt);
      else if (this.cameraMode === 'aerial') this.updateAerialCamera();
      else this.chase.update(this.kit.camera, this.viewCar, dt, this.input.lookingBack);
      const { x: lx, y: ly, z: lz } = this.kit.camera.position;
      const { x: qx, y: qy, z: qz, w: qw } = this.kit.camera.quaternion;
      carAudio.updateListener(lx, ly, lz, qx, qy, qz, qw);
      this.updateRemotes(now, dt);
      this.updateMinimap();
      this.reportState(now);
      this.emitHud(now);
    }

    // The miniature look belongs to the cameras that look down on the city — the grid's
    // fly-over, the aerial view, the outro — and eases in and out rather than cutting.
    // Never the chase camera: nobody races through a tilt-shift lens (DECISIONS.md §85).
    const tiltTarget = this.showcase.running ? 1 : this.spectating ? 0.6 : this.cameraMode === 'aerial' ? 1 : 0;
    this.tilt += (tiltTarget - this.tilt) * (1 - Math.exp(-4 * dt));
    this.kit.setTiltShift(this.tilt < 0.01 ? 0 : this.tilt);
    // The draw distance is the chase camera's alone (DECISIONS.md §92): a view from the air is
    // for seeing the region, and stopping it at 800 m would hide most of it.
    const fromAbove = this.showcase.running || this.spectating || this.cameraMode === 'aerial';
    this.kit.setViewDistance(fromAbove ? null : VIEW_DISTANCE_METRES[graphicsPrefs.viewDistance]);

    this.kit.update(dt);
    this.kit.render();
  }

  /**
   * The grid's establishing shot, and what it borrows from the scene for the length of it.
   *
   * The gates light up in turn as the drone reaches them, using the race's own colours —
   * the one ahead yellow, the ones behind spent — so the shot reads as the route being
   * walked through rather than as a camera drifting over a city. And between passes the
   * canvas dips to black: a CSS filter rather than anything in the scene, because it is a
   * cut between two shots, not something that happens in the city. The haze is the
   * shot's own too (see `FlyoverCamera`), and goes back to the weather's when it ends.
   */
  private updateShowcase(dt: number): void {
    this.showcase.update(this.kit.camera, dt);
    this.kit.setFogRange(this.showcase.fog);
    const gate = this.showcase.gatesPassed;
    if (gate !== this.showcaseGate) {
      this.showcaseGate = gate;
      this.routeMeshes?.setNextCheckpoint(gate);
    }
    const brightness = this.showcase.brightness;
    const filter = brightness < 1 ? `brightness(${brightness.toFixed(3)})` : '';
    if (this.canvas.style.filter !== filter) this.canvas.style.filter = filter;
  }

  /** Hands the HUD a street name when the car has settled onto a new one. See `StreetTracker`. */
  private updateStreet(prevX: number, prevZ: number): void {
    if (!this.streets) return;
    const name = this.streets.at(this.car.x, this.car.z, this.car.heading);
    const moved = Math.hypot(this.car.x - prevX, this.car.z - prevZ);
    const announce = this.streetTracker.update(name, moved);
    if (announce) this.callbacks.onStreet(announce, this.signStyle);
  }

  /**
   * The world's own noise, per frame.
   *
   * Everything it hands over is something the renderer or the car already worked out — the
   * weather is read back off the resolved `WeatherLook` rather than off the `Weather`, so
   * the rain you hear and the rain you can see are the same decision — and the mix itself
   * happens in `bedMix`, where it can be tested.
   *
   * The crash cues ride along here because they come off `CarState.impact`, the same figure
   * the skid in `carAudio.update` reads, and it only survives the step that produced it.
   * They are gated on actually driving so that an impact left over from the moment the
   * lights went out cannot re-fire under the countdown or the flyover.
   */
  private updateAmbience(dt: number): void {
    const look = this.kit.look;
    const precipitation = look.precipitation;
    const driving = this.input.enabled && !this.finished;

    ambience.update(
      {
        rain: precipitation?.kind === 'rain' ? precipitation.intensity : 0,
        snow: precipitation?.kind === 'snow' ? precipitation.intensity : 0,
        storm: look.lightning,
        night: look.night,
        speed: forwardSpeed(this.car),
        onRoad: this.car.onRoad,
        driving,
        // Heard where the camera is, as the cars are.
        bell: this.railMeshes?.bellAt(this.kit.camera.position.x, this.kit.camera.position.z) ?? 0,
        train: this.railMeshes?.rumbleAt(this.kit.camera.position.x, this.kit.camera.position.z) ?? 0,
      },
      dt,
    );

    if (driving && this.car.impact > CRASH_SOFT_SPEED) {
      const hard = this.car.impact > CRASH_HARD_SPEED;
      ambience.play(hard ? 'crash-hard' : 'crash-soft', Math.min(1, 0.5 + this.car.impact / 20));
    } else if (driving && this.car.kerb > KERB_THUMP_SPEED) {
      ambience.play('crash-soft', Math.min(0.6, 0.2 + this.car.kerb / 20));
    }
  }

  /**
   * A fixed high shot over the car, looking north-ish down at the city.
   *
   * It exists mostly for the visual regression test — "roads should look like roads" is
   * only answerable from above — but it is also just a nice way to see where you are.
   */
  private updateAerialCamera(): void {
    const height = this.aerialHeight;
    const car = this.viewCar;
    this.kit.camera.position.set(car.x + height * 0.35, car.y + height, car.z + height * 0.62);
    this.kit.camera.lookAt(car.x, car.y, car.z);
    if (this.kit.camera.fov !== 55) {
      this.kit.camera.fov = 55;
      this.kit.camera.updateProjectionMatrix();
    }
  }

  cameraMode: 'chase' | 'aerial' = 'chase';
  /** How far in the miniature look is, eased towards what the camera asks for. */
  private tilt = 0;
  aerialHeight = 220;

  /**
   * Hands the wheel to the route-following controller. Used by the end-to-end tests to
   * complete a real race, and available in-game as a demo/"watch the city" mode.
   */
  autopilot = false;
  autopilotPace = 1;

  setCameraMode(mode: 'chase' | 'aerial', height = 220): void {
    this.cameraMode = mode;
    this.aerialHeight = height;
    this.chase.reset();
  }

  private trackPerf(dt: number): void {
    this.frameTimes.push(dt);
    // Judged every 240 frames over the 240 before, as it always was; only the reading no
    // longer throws the samples away.
    if (++this.framesSinceCheck < PERF_WINDOW) return;
    this.framesSinceCheck = 0;
    const fps = fpsOf(this.frameTimes, PERF_WINDOW);
    // Two windows running, not one: the first seconds of a race include shaders compiling
    // and the world settling, and a single slow window then is not a slow machine.
    this.slowWindows = fps < AUTO_LOW_FPS ? this.slowWindows + 1 : 0;
    if (this.slowWindows >= 2 && graphicsPrefs.lowerForSpeed()) {
      this.slowWindows = 0;
      this.callbacks.onPerfWarning(t('Graphics dropped to Low to keep the race smooth. Esc to change it.'));
      return;
    }
    if (fps < 40 && !this.perfWarned) {
      this.perfWarned = true;
      this.callbacks.onPerfWarning(
        t('Running at ~{fps} fps with {triangles} triangles. Try a smaller region.', {
          fps: Math.round(fps),
          triangles: num(this.triangleCount),
        }),
      );
    }
  }

  /** Over the last 120 frames: a second or two, steady enough to read and quick to follow. */
  get fps(): number {
    return fpsOf(this.frameTimes, 120);
  }

  private syncCarMesh(): void {
    if (!this.carMesh) return;
    this.carMesh.group.position.set(this.viewCar.x, this.viewCar.y, this.viewCar.z);
    this.carMesh.group.rotation.y = this.viewCar.heading;
  }

  /**
   * Local, optimistic checkpoint tracking so the HUD reacts instantly. The server runs
   * exactly the same test against the positions we report, and its answer is the one that
   * decides the race — this copy only exists so the arrow moves without a round trip.
   */
  private checkProgress(prevX: number, prevZ: number): void {
    const route = this.world?.route;
    if (!route || this.finished || this.freeDrive) return;

    if (this.nextCheckpoint < route.checkpoints.length) {
      // The same segment test the server runs on the reports, so the two copies agree on
      // a gate the car only clipped the edge of.
      const gate = route.checkpoints[this.nextCheckpoint];
      if (passedThrough(gate, prevX, prevZ, this.car.x, this.car.z)) {
        this.nextCheckpoint++;
        this.routeMeshes?.setNextCheckpoint(this.nextCheckpoint);
        // Optimistic, like the banner it goes with: this is the local test, and the server
        // is the one that decides. A gate sounding a frame before the server agrees is the
        // whole point of keeping this copy at all.
        ambience.play('checkpoint');
      }
      return;
    }

    // The finish is a line, not a circle — see `crossedFinish`. Tested against the whole
    // step, so a car doing 150 km/h cannot straddle it between two frames.
    if (crossedFinish(route.finish, prevX, prevZ, this.car.x, this.car.z)) {
      this.finished = true;
      this.input.enabled = false;
      ambience.play('finish');
      this.beginSpectating();
      this.callbacks.onLocalFinish();
    }
  }

  /**
   * Everyone else, as far as this frame's collision pass is concerned: each remote's own
   * interpolated pose, the same one `updateRemotes` is about to render it at.
   *
   * Deliberately not `remote.mesh.group.position` — that only holds *last* frame's answer,
   * since `updateRemotes` runs after this in `frame()`. Recomputing here costs one pass
   * over a handful of remotes, not a restructure of the render order.
   */
  private remoteCars(now: number): OtherCar[] {
    if (this.remotes.size === 0) return [];
    const renderAt = now - INTERP_DELAY_MS;
    const cars: OtherCar[] = [];
    for (const remote of this.remotes.values()) {
      if (!remote.samples.length) continue;
      // A car nobody is updating any more is not something you can crash into.
      //
      // The server stops putting a disconnected player in the snapshot stream but holds
      // their slot for a minute so they can rejoin, so their car stays on screen frozen
      // wherever they dropped. That was harmless scenery until cars became solid; then it
      // became a parked wreck in the middle of the racing line that never clears, and since
      // neither the autopilot nor the AI has any notion of going *around* something, one
      // dropped connection could wall the route off for everybody still driving. Measured:
      // it hung a race until the twelve-minute timeout.
      //
      // It is still drawn — a car that vanishes mid-corner is its own kind of wrong — it
      // simply stops being solid.
      if (now - remote.samples[remote.samples.length - 1].t > STALE_CAR_MS) continue;
      const pos = interpolateRemote(remote.samples, renderAt);
      cars.push({ x: pos.x, z: pos.z, heading: pos.h, speed: remote.speed });
    }
    return cars;
  }

  private updateRemotes(now: number, dt: number): void {
    const renderAt = now - INTERP_DELAY_MS;
    for (const [id, remote] of this.remotes) {
      if (!remote.samples.length) continue;
      const pos = interpolateRemote(remote.samples, renderAt);

      remote.mesh.group.position.set(pos.x, pos.y, pos.z);
      remote.mesh.group.rotation.y = pos.h;
      this.updateBeacon(remote, pos);
      remote.mesh.animate(remote.speed, remote.lastSteer, 0, dt);
      // `remote.speed` is the raw signed `v` off the wire, unlike the interpolated
      // position above — that is what lets the audio layer tell a real speed change (a new
      // network sample) from a render frame that merely interpolated between two old ones.
      carAudio.updateRemote(id, pos.x, pos.y, pos.z, remote.speed, now, dt);
    }
    carAudio.finalizeRemoteAudibility(dt);
  }

  /**
   * In a free drive the other cars are what you are looking for, and a city of six-storey
   * blocks hides a car a street away. So each one carries the finish line's column of light,
   * in its own colour. It fades out as you get close, where it would stand in the windscreen,
   * and it is not there in a race, where the finish is the one beacon that matters.
   */
  private updateBeacon(remote: RemoteCar, pos: { x: number; y: number; z: number }): void {
    if (!this.freeDrive) {
      if (remote.beacon) remote.beacon.mesh.visible = false;
      return;
    }
    if (!remote.beacon) {
      remote.beacon = buildBeacon(remote.color);
      remote.beacon.mesh.name = 'player-beacon';
      this.kit.scene.add(remote.beacon.mesh);
    }
    const camera = this.kit.camera.position;
    const distance = Math.hypot(camera.x - pos.x, camera.z - pos.z);
    const material = remote.beacon.material as THREE.MeshBasicMaterial;
    material.opacity = 0.5 * Math.min(1, Math.max(0, (distance - BEACON_NEAR) / BEACON_FADE));
    remote.beacon.mesh.visible = material.opacity > 0.01;
    remote.beacon.mesh.position.set(pos.x, pos.y + 2, pos.z);
  }

  private disposeBeacon(remote: RemoteCar): void {
    if (!remote.beacon) return;
    this.kit.scene.remove(remote.beacon.mesh);
    remote.beacon.geometry.dispose();
    remote.beacon.material.dispose();
    remote.beacon = null;
  }

  private updateMinimap(): void {
    const players: MinimapPlayer[] = (this.mapPlayers = [
      {
        x: this.car.x,
        z: this.car.z,
        heading: this.car.heading,
        color: this.localColor,
        self: true,
      },
    ]);
    for (const [id, remote] of this.remotes) {
      const last = remote.samples[remote.samples.length - 1];
      if (!last) continue;
      players.push({
        x: last.x,
        z: last.z,
        heading: last.h,
        color: remote.color,
        self: false,
        name: this.roster.find((p) => p.id === id)?.name,
      });
    }
    this.minimap.draw(players, this.nextCheckpoint);
  }

  /** What the street map draws: the same cars the minimap last drew, and the route. */
  mapSnapshot(): MapSnapshot {
    return {
      world: this.world,
      players: this.mapPlayers,
      nextCheckpoint: this.nextCheckpoint,
      freeDrive: this.freeDrive,
    };
  }

  /**
   * Hands the keyboard to a menu. The race carries on — this is multiplayer, and nobody
   * else stops — the car simply gets no input, which is a car coasting, not a car frozen.
   */
  setControlsBlocked(blocked: boolean): void {
    this.input.blocked = blocked;
    if (blocked) this.input.clear();
  }

  // --- minimap controls ------------------------------------------------------
  // The map is the game's, not the HUD's — the HUD only owns the canvas it draws on — so
  // the buttons and the keyboard both come through here.

  zoomMinimap(step: number): void {
    this.minimap.zoomBy(step);
  }

  toggleMinimapMode(): MinimapMode {
    return this.minimap.toggleMode();
  }

  minimapControls(): { mode: MinimapMode; canZoomIn: boolean; canZoomOut: boolean } {
    return {
      mode: this.minimap.currentMode,
      canZoomIn: this.minimap.canZoom(1),
      canZoomOut: this.minimap.canZoom(-1),
    };
  }

  private reportState(now: number): void {
    const interval = 1000 / STATE_HZ;
    if (now - this.lastStateSent < interval) return;
    // On a schedule, not "50 ms since the last one": that rounds every gap up to a whole
    // frame, so the real rate depended on the monitor — a 60 fps client whose third frame
    // landed a hair short of 50 ms waited for a fourth and reported at 15 Hz.
    // After a stall, start the schedule again rather than sending a burst to catch up.
    this.lastStateSent =
      now - this.lastStateSent > 2 * interval ? now : this.lastStateSent + interval;
    this.callbacks.onState(
      this.car.x,
      this.car.y,
      this.car.z,
      this.car.heading,
      forwardSpeed(this.car),
    );
  }

  private emitHud(now: number): void {
    const route = this.world?.route;
    if (!route) return;
    const target =
      this.nextCheckpoint < route.checkpoints.length
        ? route.checkpoints[this.nextCheckpoint]
        : route.finish;

    const countdownMs = this.countdownUntil === null ? null : this.countdownUntil - now;
    const myProgress = projectOntoPolyline(route.path, this.car.x, this.car.z).distance;
    let position = 1;
    for (const player of this.roster) {
      if (player.id === this.localId) continue;
      if (player.finishedAt !== null || player.progress > myProgress) position++;
    }
    // A place changing hands, off the same figure the HUD is about to show. Only while the
    // race is actually running: everyone's placing shuffles as the field crosses the line,
    // and none of that is something the player did.
    if (
      this.input.enabled &&
      !this.finished &&
      !this.freeDrive &&
      this.lastPosition > 0 &&
      position !== this.lastPosition
    ) {
      ambience.play(position < this.lastPosition ? 'overtake' : 'overtaken');
    }
    this.lastPosition = position;

    this.callbacks.onHud({
      speedKmh: Math.abs(forwardSpeed(this.car)) * 3.6,
      onRoad: this.car.onRoad,
      checkpointsPassed: this.nextCheckpoint,
      checkpointsTotal: route.checkpoints.length + 1,
      raceTimeMs:
        this.raceStartAt !== null && countdownMs === null ? Math.max(0, now - this.raceStartAt) : 0,
      position,
      playerCount: Math.max(1, this.roster.length),
      countdown: countdownMs !== null && countdownMs > -1000 ? Math.ceil(countdownMs / 1000) : null,
      finished: this.finished,
      distanceToNext: Math.hypot(this.car.x - target.x, this.car.z - target.z),
      spectating: this.spectating,
      spectatingName: this.spectating ? this.spectatingName : null,
      staging: this.showcase.running,
      freeDrive: this.freeDrive,
      street: this.streetTracker.current,
    });
  }

  /**
   * Puts a stuck car back on the racing line without losing checkpoint progress — or, in a
   * free drive, on the nearest street, which is where somebody exploring the city wants to
   * carry on from rather than back on a route they were never following.
   */
  respawn(): void {
    const route = this.world?.route;
    if (!route) return;
    if (this.freeDrive) {
      const street = this.nearestStreet();
      if (street) {
        this.setSpawn(street.x, street.z, street.heading);
        ambience.play('respawn');
        return;
      }
    }
    const hit = projectOntoPolyline(route.path, this.car.x, this.car.z);
    const path = route.path;
    // Walk the path to the closest arc-length, then face along it.
    let acc = 0;
    let x = route.start.x;
    let z = route.start.z;
    let dx = route.start.dx;
    let dz = route.start.dz;
    for (let i = 2; i < path.length; i += 2) {
      const segLen = Math.hypot(path[i] - path[i - 2], path[i + 1] - path[i - 1]);
      if (acc + segLen >= hit.distance) {
        const k = segLen > 0 ? (hit.distance - acc) / segLen : 0;
        x = path[i - 2] + (path[i] - path[i - 2]) * k;
        z = path[i - 1] + (path[i + 1] - path[i - 1]) * k;
        dx = (path[i] - path[i - 2]) / Math.max(1e-6, segLen);
        dz = (path[i + 1] - path[i - 1]) / Math.max(1e-6, segLen);
        break;
      }
      acc += segLen;
    }
    this.setSpawn(x, z, headingFromDirection(dx, dz));
    ambience.play('respawn');
  }

  /** The closest point on any drivable road, facing the way the car already was along it. */
  private nearestStreet(): { x: number; z: number; heading: number } | null {
    const world = this.world;
    if (!world) return null;
    const { x, z } = this.car;
    let best: { x: number; z: number; heading: number } | null = null;
    let bestD2 = Infinity;
    for (const road of world.roads) {
      if (!ROAD_SPECS[road.cls].drivable) continue;
      const pts = road.pts;
      for (let i = 2; i < pts.length; i += 2) {
        const ax = pts[i - 2];
        const az = pts[i - 1];
        const dx = pts[i] - ax;
        const dz = pts[i + 1] - az;
        const len2 = dx * dx + dz * dz;
        if (len2 < 1e-6) continue;
        const k = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / len2));
        const px = ax + dx * k;
        const pz = az + dz * k;
        const d2 = (px - x) ** 2 + (pz - z) ** 2;
        if (d2 >= bestD2) continue;
        bestD2 = d2;
        // Whichever way along the street is closer to where the car was pointing.
        const along = headingFromDirection(dx, dz);
        const flip = Math.abs(shortestAngle(this.car.heading, along)) > Math.PI / 2;
        best = { x: px, z: pz, heading: flip ? headingFromDirection(-dx, -dz) : along };
      }
    }
    return best;
  }

  get inputController(): InputController {
    return this.input;
  }

  get carState(): CarState {
    return this.car;
  }

  get tuning() {
    return DEFAULT_TUNING;
  }

  dispose(): void {
    this.stop();
    carAudio.silence();
    ambience.silence();
    this.clearWorld();
    if (this.carMesh) {
      this.kit.scene.remove(this.carMesh.group);
      this.carMesh.dispose();
      this.carMesh = null;
    }
    this.input.dispose();
    this.touchControls?.dispose();
    this.unsubscribeGraphics();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.handleResize);
    this.kit.dispose();
  }
}

function shortestAngle(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d <= -Math.PI) d += Math.PI * 2;
  return d;
}

/**
 * The same interpolated pose `updateRemotes` renders a remote car at, for `renderAt`.
 *
 * Pulled out so the collision pass can ask "where does this remote actually appear to be"
 * without duplicating the interpolation — a remote's mesh position is already exactly this
 * answer, one frame stale, but `frame()` needs it *before* `updateRemotes` runs this tick,
 * not after.
 */
function interpolateRemote(samples: RemoteSample[], renderAt: number): RemoteSample {
  if (samples.length === 1 || renderAt <= samples[0].t) return samples[0];

  let a = samples[0];
  let b = samples[samples.length - 1];
  for (let i = 1; i < samples.length; i++) {
    if (samples[i].t >= renderAt) {
      a = samples[i - 1];
      b = samples[i];
      break;
    }
  }
  const span = b.t - a.t;
  const k = span > 1 ? Math.min(1, (renderAt - a.t) / span) : 1;
  return {
    t: renderAt,
    x: a.x + (b.x - a.x) * k,
    y: a.y + (b.y - a.y) * k,
    z: a.z + (b.z - a.z) * k,
    // Shortest-arc so a car crossing the ±PI seam doesn't spin on the spot.
    h: a.h + shortestAngle(a.h, b.h) * k,
  };
}
